export const CPF_DIGITS_LENGTH = 11;
export const CNPJ_DIGITS_LENGTH = 14;
export const CNPJ_ALPHANUMERIC_REGEX = /^[0-9A-Z]{12}\d{2}$/;

const DOCUMENT_IDENTIFIER_PATTERN = /^[0-9A-Za-z.\-/]+$/;

export const normalizeCNPJ = (value) =>
  String(value ?? '')
    .replace(/[^0-9A-Za-z]/g, '')
    .toUpperCase();

const takeCnpjInputChars = (value) => {
  const body = [];
  const checkDigits = [];

  String(value ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .split('')
    .forEach((char) => {
      if (body.length < 12) {
        body.push(char);
        return;
      }
      if (/\d/.test(char) && checkDigits.length < 2) {
        checkDigits.push(char);
      }
    });

  return `${body.join('')}${checkDigits.join('')}`;
};

export const formatCNPJInput = (value) => {
  const raw = takeCnpjInputChars(value);

  if (raw.length <= 2) return raw;
  if (raw.length <= 5) return `${raw.slice(0, 2)}.${raw.slice(2)}`;
  if (raw.length <= 8) {
    return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5)}`;
  }
  if (raw.length <= 12) {
    return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5, 8)}/${raw.slice(8)}`;
  }
  return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5, 8)}/${raw.slice(8, 12)}-${raw.slice(12)}`;
};

export const formatCpfInput = (value) => {
  const raw = String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, CPF_DIGITS_LENGTH);

  if (raw.length <= 3) return raw;
  if (raw.length <= 6) return `${raw.slice(0, 3)}.${raw.slice(3)}`;
  if (raw.length <= 9) {
    return `${raw.slice(0, 3)}.${raw.slice(3, 6)}.${raw.slice(6)}`;
  }
  return `${raw.slice(0, 3)}.${raw.slice(3, 6)}.${raw.slice(6, 9)}-${raw.slice(9)}`;
};

export const formatDocumentInput = (value) => {
  const normalized = normalizeCNPJ(value);
  if (/[A-Z]/.test(normalized) || normalized.length > CPF_DIGITS_LENGTH) {
    return formatCNPJInput(value);
  }
  return formatCpfInput(value);
};

export const isValidCNPJFormat = (value) =>
  CNPJ_ALPHANUMERIC_REGEX.test(normalizeCNPJ(value));

export const formatCNPJ = (value) => {
  const normalized = normalizeCNPJ(value);

  if (!CNPJ_ALPHANUMERIC_REGEX.test(normalized)) {
    return String(value ?? '');
  }

  return normalized.replace(
    /^([0-9A-Z]{2})([0-9A-Z]{3})([0-9A-Z]{3})([0-9A-Z]{4})(\d{2})$/,
    '$1.$2.$3/$4-$5',
  );
};

export const matchesCnpjSearch = (cnpj, keyword) => {
  const needle = normalizeCNPJ(keyword);
  if (!needle) return false;
  return normalizeCNPJ(cnpj).includes(needle);
};

export const normalizeDocumentIdentifier = (identifier) => {
  const trimmed = String(identifier ?? '').trim();
  if (!trimmed || !DOCUMENT_IDENTIFIER_PATTERN.test(trimmed)) {
    return trimmed;
  }

  const normalizedCnpj = normalizeCNPJ(trimmed);
  if (isValidCNPJFormat(normalizedCnpj)) {
    return normalizedCnpj;
  }

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === CPF_DIGITS_LENGTH) {
    return digits;
  }

  return trimmed;
};

const cnpjCharValue = (char) => char.charCodeAt(0) - 48;

export const validateCPF = (cpf) => {
  let sum;
  let rest;
  sum = 0;
  if (cpf === '00000000000' || /^(.)\1+$/.test(cpf)) {
    return false;
  }

  for (let i = 1; i <= 9; i++)
    sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  rest = (sum * 10) % 11;

  if (rest === 10 || rest === 11) rest = 0;
  if (rest !== parseInt(cpf.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++)
    sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  rest = (sum * 10) % 11;

  if (rest === 10 || rest === 11) rest = 0;
  if (rest !== parseInt(cpf.substring(10, 11))) return false;
  return true;
};

export const validateCNPJ = (cnpj) => {
  if (cnpj === undefined || cnpj === null) return false;

  const normalized = normalizeCNPJ(cnpj);
  if (!CNPJ_ALPHANUMERIC_REGEX.test(normalized)) return false;
  if (/^(.)\1+$/.test(normalized)) return false;

  let size = normalized.length - 2;
  let numbers = normalized.substring(0, size);
  const digits = normalized.substring(size);
  let sum = 0;
  let pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += cnpjCharValue(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  let resultado = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  if (String(resultado) !== digits.charAt(0)) return false;

  size += 1;
  numbers = normalized.substring(0, size);
  sum = 0;
  pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += cnpjCharValue(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  resultado = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (String(resultado) !== digits.charAt(1)) return false;

  return true;
};
